import React, { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import './Table.css';

export interface TableColumn<T = any> {
  key: string;
  label: string;
  width?: string | number; // e.g., '120px', 1 (flex), 'auto'
  align?: 'left' | 'center' | 'right';
  render?: (item: T) => ReactNode;
  hideOnMobile?: boolean;
}

export interface TableProps<T = any> {
  columns: TableColumn<T>[];
  data: T[];
  keyExtractor: (item: T) => string | number;
  onRowClick?: (item: T) => void;
  rowClassName?: (item: T) => string;
  emptyMessage?: string;
  loading?: boolean;
  className?: string;
}

function Table<T = any>({
  columns,
  data,
  keyExtractor,
  onRowClick,
  rowClassName,
  emptyMessage = 'No data available',
  loading = false,
  className = '',
}: TableProps<T>) {
  const getColumnStyle = (column: TableColumn<T>) => {
    const style: React.CSSProperties = {};
    
    if (typeof column.width === 'number') {
      style.flex = column.width;
      style.minWidth = 0;
    } else if (column.width) {
      style.width = column.width;
      style.flexShrink = 0;
    } else {
      style.flex = 1;
      style.minWidth = 0;
    }

    if (column.align) {
      style.justifyContent = column.align === 'left' ? 'flex-start' : column.align === 'right' ? 'flex-end' : 'center';
    }

    return style;
  };

  return (
    <Box className={`table-container ${className}`}>
      {/* Table Header - Desktop Only */}
      <Box className="table-header">
        {columns.map((column) => (
          <Box
            key={column.key}
            className={`table-header-column ${column.hideOnMobile ? 'hide-mobile' : ''}`}
            style={getColumnStyle(column)}
          >
            <Typography className="table-header-label">
              {column.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Table Body with Scroll */}
      <Box className="table-body">
        {loading ? (
          <Box className="table-loading-state">
            <Box className="table-loading-skeleton">
              {Array.from({ length: 5 }).map((_, index) => (
                <Box key={index} className="table-loading-row">
                  {columns.map((column) => (
                    <Box
                      key={column.key}
                      className="table-loading-cell"
                      style={getColumnStyle(column)}
                    />
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        ) : data.length === 0 ? (
          <Box className="table-empty-state">
            <Typography className="table-empty-text">
              {emptyMessage}
            </Typography>
          </Box>
        ) : (
          data.map((item) => {
            const rowKey = keyExtractor(item);
            const customClassName = rowClassName ? rowClassName(item) : '';
            const isClickable = onRowClick ? 'clickable' : '';

            return (
              <Box
                key={rowKey}
                className={`table-row ${isClickable} ${customClassName}`}
                onClick={() => onRowClick?.(item)}
              >
                {columns.map((column) => (
                  <Box
                    key={column.key}
                    className={`table-cell table-cell-${column.key} ${column.hideOnMobile ? 'hide-mobile' : ''}`}
                    style={getColumnStyle(column)}
                  >
                    {column.render ? column.render(item) : null}
                  </Box>
                ))}
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}

export default Table;
